'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Card } from '@/components/ui/card'
import { MapPin, Bed, Bath, Home, ArrowRight, Building2 } from 'lucide-react'
import { formatPrice } from '@/lib/formatters'

interface Condo {
  rank: number
  id: string
  slug: string
  title: string
  city: string
  price: number
  bedrooms: number
  bathrooms: number
  primaryImageUrl: string | null
  description?: string
}

export default function Top10CondosForRentClient() {
  const [condos, setCondos] = useState<Condo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetch('/api/top-10/condos-for-rent')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch condos')
        return res.json()
      })
      .then(data => {
        setCondos(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error loading condos:', err)
        setError(err.message)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <section className="py-8 md:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:gap-8">
            {[...Array(10)].map((_, i) => (
              <Card key={i} className="overflow-hidden bg-white border-0 shadow-lg">
                <div className="md:flex md:flex-row">
                  <div className="relative w-full md:w-2/5 lg:w-1/3 h-64 md:min-h-[400px] bg-gray-200 animate-pulse flex-shrink-0" />
                  <div className="flex-1 p-4 md:p-6 lg:p-8 space-y-4">
                    <div className="h-8 bg-gray-200 rounded animate-pulse w-3/4" />
                    <div className="h-6 bg-gray-200 rounded animate-pulse w-1/2" />
                    <div className="h-10 bg-gray-200 rounded animate-pulse w-1/3" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-8 md:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <p className="text-red-600">Failed to load properties. Please try again later.</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-8 md:py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-6 md:gap-8">
          {condos.map((condo) => (
            <Link href={`/property/${condo.slug}`} key={condo.rank}>
              <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 group bg-white border-0 shadow-lg">
                {/* Mobile Layout: Image on top, content below */}
                <div className="md:flex md:flex-row">
                  {/* Property Image */}
                  <div className="relative w-full md:w-2/5 lg:w-1/3 h-64 md:h-auto md:min-h-[400px] bg-gray-200 flex-shrink-0">
                    {condo.primaryImageUrl ? (
                      <Image
                        src={condo.primaryImageUrl}
                        alt={condo.title}
                        fill
                        loading={condo.rank === 1 ? "eager" : "lazy"}
                        priority={condo.rank === 1}
                        quality={75}
                        className="object-cover group-hover:scale-105 transition-transform duration-500"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 40vw, 33vw"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                        <Building2 className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                    {/* Rank Badge - Overlay on mobile */}
                    <div className="absolute top-4 left-4 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#d60500] to-[#8b0300] rounded-xl shadow-2xl flex items-center justify-center">
                      <div className="text-center text-white">
                        <div className="text-3xl md:text-4xl font-bold">{condo.rank}</div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 md:p-6 lg:p-8">
                    {/* Title */}
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 md:mb-4 group-hover:text-[#d60500] transition-colors line-clamp-2">
                      {condo.title}
                    </h3>

                    {/* Location */}
                    <div className="flex items-center gap-2 text-gray-600 mb-3 md:mb-4">
                      <MapPin className="w-4 h-4 md:w-5 md:h-5 text-[#d60500] flex-shrink-0" />
                      <span className="text-sm md:text-base lg:text-lg">{condo.city}</span>
                    </div>

                    {/* Price */}
                    <div className="mb-4 md:mb-6">
                      <div className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#d60500]">
                        {formatPrice(condo.price)}
                      </div>
                      <div className="text-sm md:text-base text-gray-600 font-medium mt-1">
                        per month
                      </div>
                    </div>

                    {/* Bedrooms & Bathrooms */}
                    <div className="flex gap-4 md:gap-6 mb-4 md:mb-6 pb-4 md:pb-6 border-b border-gray-200">
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                          <Bed className="w-5 h-5 md:w-6 md:h-6 text-[#d60500]" />
                        </div>
                        <div>
                          <div className="text-xs md:text-sm text-gray-600">Bedrooms</div>
                          <div className="text-lg md:text-xl font-bold text-gray-900">{condo.bedrooms}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 md:gap-3">
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                          <Bath className="w-5 h-5 md:w-6 md:h-6 text-[#d60500]" />
                        </div>
                        <div>
                          <div className="text-xs md:text-sm text-gray-600">Bathrooms</div>
                          <div className="text-lg md:text-xl font-bold text-gray-900">{condo.bathrooms}</div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {condo.description && (
                      <p className="text-sm md:text-base lg:text-lg text-gray-700 mb-4 md:mb-6 leading-relaxed line-clamp-3">
                        {condo.description}
                      </p>
                    )}

                    {/* View Details Button */}
                    <button className="inline-flex items-center gap-2 px-6 py-3 bg-[#d60500] text-white font-semibold text-sm md:text-base rounded-lg hover:bg-[#b50400] transition-all group-hover:gap-3">
                      View Full Rental Details
                      <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}
